/**
 * Single source of truth for PODGO's SEO & AI-search configuration.
 *
 * The static route catalog and sensible defaults live here in code; admin
 * overrides (per-route index/noindex + title/description, site defaults, and
 * the AI summary) are stored as a JSON blob in `admin_settings` under
 * `SEO_CONFIG_KEY`. Every consumer (runtime <SeoManager>, the Theme Options
 * editor, the live sitemap edge function, and the build-time generator) reads
 * the same shape and merges DB overrides over these defaults, so there is one
 * authoritative model rather than parallel copies.
 */

export const SEO_CONFIG_KEY = "seo_config";

export const SITE_ORIGIN = "https://podgo.io";

export interface RouteSeo {
  /** Whether the page is indexable (sitemap inclusion + robots meta). */
  index: boolean;
  /** Optional per-page title override (replaces the default title). */
  title?: string;
  /** Optional per-page meta-description override. */
  description?: string;
}

export interface SeoDefaults {
  /** Pattern for per-page titles; `%s` is replaced by the page title. */
  titleTemplate: string;
  defaultTitle: string;
  defaultDescription: string;
  /** Absolute URL of the default social share image. */
  ogImage: string;
  organizationName: string;
  /** Plain-text summary served at /llms.txt for AI search engines. */
  aiSummary: string;
}

export interface SeoConfig {
  defaults: SeoDefaults;
  routes: Record<string, RouteSeo>;
}

/** A public route in the catalog, with sitemap hints. */
export interface PublicRoute {
  path: string;
  label: string;
  changefreq: "daily" | "weekly" | "monthly" | "yearly";
  priority: string;
}

/**
 * Public, indexable static routes. Auth, onboarding, dashboard and admin
 * routes are intentionally excluded — they must never appear in the sitemap.
 * Keep this aligned with the public routes in src/App.tsx.
 */
export const PUBLIC_ROUTES: PublicRoute[] = [
  { path: "/", label: "Home", changefreq: "weekly", priority: "1.0" },
  { path: "/about", label: "About", changefreq: "monthly", priority: "0.8" },
  { path: "/blog", label: "Blog", changefreq: "weekly", priority: "0.8" },
  { path: "/knowledgebase", label: "Knowledgebase", changefreq: "weekly", priority: "0.6" },
  {
    path: "/why-you-should-join-podgo",
    label: "Why You Should Join PODGO",
    changefreq: "monthly",
    priority: "0.8",
  },
  { path: "/podcasts", label: "Browse Podcasts", changefreq: "daily", priority: "0.9" },
  { path: "/studios", label: "Studios", changefreq: "weekly", priority: "0.8" },
  { path: "/careers", label: "Careers", changefreq: "weekly", priority: "0.6" },
  { path: "/help", label: "Help Center", changefreq: "monthly", priority: "0.5" },
  { path: "/terms", label: "Terms of Service", changefreq: "yearly", priority: "0.3" },
  { path: "/privacy", label: "Privacy Policy", changefreq: "yearly", priority: "0.3" },
  { path: "/dmca", label: "DMCA Policy", changefreq: "yearly", priority: "0.3" },
  { path: "/cookies", label: "Cookie Policy", changefreq: "yearly", priority: "0.3" },
  { path: "/gdpr", label: "GDPR", changefreq: "yearly", priority: "0.3" },
];

export const DEFAULT_SEO_DEFAULTS: SeoDefaults = {
  titleTemplate: "%s | PODGO",
  defaultTitle: "PODGO | Your Podcast. Everywhere.",
  defaultDescription:
    "Your podcast, distributed everywhere. Upload once, reach every platform.",
  ogImage: `${SITE_ORIGIN}/og-image.png`,
  organizationName: "PODGO",
  aiSummary: `# PODGO

> PODGO is a podcast operations platform. A podcaster uploads an episode once and
> PODGO hosts the audio, manages the RSS feed, distributes the episode to every
> major listening platform, reports downloads and revenue in one dashboard, sells
> advertising against the show, and books studio time. PODGO is podcasts only -
> no music, artists, albums or tracks. The brand name is always written PODGO, in
> capitals; never "PodGo" and never "Podgo".

## What PODGO does

- Hosting and RSS - RSS hosting is included on every plan, including the free
  plan. PODGO manages the feed; custom RSS settings are available on higher tiers.
- One-click distribution - one upload publishes to Spotify, Apple Podcasts,
  Amazon Music and other listening platforms. Selected platforms go live in under
  24 hours. Initial setup takes under 2 minutes.
- Analytics - real-time downloads, listener geography, device and app breakdown,
  audience growth trends.
- Monetization - host-read endorsements, dynamically inserted ads, sponsorships
  and branded content, sold by PODGO's in-house ad sales team. Earnings from every
  platform appear in one dashboard with payout history and collaborator revenue
  splits.
- Studios - professional podcast recording rooms bookable on demand in New York
  and Nashville.
- Networks and teams - multi-show management, scheduled publishing, team
  collaboration, ad marker support, and API access on the top tier.

Plans, prices and revenue-share splits are published on the home page rather than
in this file, so they are always read from the live pricing section.

## Key pages

- [Home](${SITE_ORIGIN}/): product overview, plan comparison, prices and
  revenue-share splits.
- [Why join PODGO](${SITE_ORIGIN}/why-you-should-join-podgo): what PODGO operates
  on a podcaster's behalf, feed and IP ownership, month-to-month terms.
- [About PODGO](${SITE_ORIGIN}/about): the company and its three pillars -
  advertising, distribution and production.
- [Browse podcasts](${SITE_ORIGIN}/podcasts): searchable directory of every show
  on the platform.
- [PODGO Studios](${SITE_ORIGIN}/studios): bookable recording studios, including
  Chelsea Studios in New York and East Iris Studios in Nashville.
- [News and updates](${SITE_ORIGIN}/blog): platform announcements and show
  launches.
- [Help and support](${SITE_ORIGIN}/help): documentation, FAQs and support
  tickets.
- [Careers](${SITE_ORIGIN}/careers): open roles.
- [Book a walkthrough](${SITE_ORIGIN}/booking/walkthrough): a 20-minute call
  reviewing your current show and numbers.
- [Sign up](${SITE_ORIGIN}/auth?tab=signup): create a PODGO account.

## Podcast directory

PODGO publishes a page for every show on the platform at
${SITE_ORIGIN}/podcast/<show-slug>, carrying the show description, host, episode
list and playable episodes. Every show page is listed in
[sitemap.xml](${SITE_ORIGIN}/sitemap.xml).

## What PODGO is not

- PODGO is not a music service. It does not host music, artists, albums or tracks.
- PODGO is not PODGO Media LLC, founded 2020, whose assets AdvertiseCast (a
  Liberated Syndication subsidiary) acquired on 4 November 2021. That was a
  membership-based flat-rate podcast ad marketplace for shows averaging under
  5,000 listeners per episode. PODGO today, at ${SITE_ORIGIN}, is a full podcast
  hosting, distribution, analytics, monetization and studio-booking platform and
  is unrelated to that entity.
- PODGO is not free-only. There is a permanent free plan and paid plans above it.

## Contact

- Support and sales: ${SITE_ORIGIN}/help
- Email: support@podgo.io
- Company information: ${SITE_ORIGIN}/about`,
};

/** Default config: every public route indexable, no per-route overrides. */
export const DEFAULT_SEO_CONFIG: SeoConfig = {
  defaults: DEFAULT_SEO_DEFAULTS,
  routes: Object.fromEntries(
    PUBLIC_ROUTES.map((r) => [r.path, { index: true } as RouteSeo]),
  ),
};

/**
 * Merge a stored (partial / possibly stale) config over the code defaults so
 * newly added routes always appear and missing fields fall back gracefully.
 */
export function mergeSeoConfig(raw: unknown): SeoConfig {
  const stored = (typeof raw === "object" && raw !== null ? raw : {}) as Partial<SeoConfig>;
  const routes: Record<string, RouteSeo> = { ...DEFAULT_SEO_CONFIG.routes };
  if (stored.routes && typeof stored.routes === "object") {
    for (const [path, value] of Object.entries(stored.routes)) {
      if (!value || typeof value !== "object") continue;
      routes[path] = {
        index: value.index !== false,
        title: value.title?.trim() || undefined,
        description: value.description?.trim() || undefined,
      };
    }
  }
  return {
    defaults: { ...DEFAULT_SEO_DEFAULTS, ...(stored.defaults ?? {}) },
    routes,
  };
}

/** Parse the raw `admin_settings.value` string into a SeoConfig. */
export function parseSeoConfig(value: string | null | undefined): SeoConfig {
  if (!value) return DEFAULT_SEO_CONFIG;
  try {
    return mergeSeoConfig(JSON.parse(value));
  } catch {
    return DEFAULT_SEO_CONFIG;
  }
}
